/**
 * Parse Movie Magic / crew-list CSV exports (client-side only).
 * Expected columns: Name, Email, Job Title, Department, Project Name, Country,
 * Schedule Start, Schedule End (header names are flexible).
 * Optional: Phone, Production Country, Location.
 */

const HEADER_ALIASES = {
  name: ['name'],
  email: ['email', 'e-mail', 'e mail', 'email address', 'e-mail address', 'work email', 'crew email'],
  'job title': ['job title', 'jobtitle', 'title'],
  department: ['department', 'dept'],
  'project name': ['project name', 'project'],
  country: ['country', 'nationality', 'country of person'],
  'production country': ['production country', 'shoot country', 'shooting country', 'destination country', 'production location'],
  'schedule start': ['schedule start', 'schedule start date', 'start date', 'shooting start', 'shoot start'],
  'schedule end': ['schedule end', 'schedule end date', 'end date', 'shooting end', 'shoot end'],
};

const OPTIONAL_HEADER_ALIASES = {
  location: [
    'location',
    'city',
    'shoot location',
    'filming location',
    'first location',
    'unit location',
    'set location',
    'primary location',
    'work location',
  ],
  phone: ['phone', 'phone number', 'phone no', 'mobile', 'mobile phone', 'cell', 'telephone', 'tel'],
};

const REQUIRED_HEADERS = Object.keys(HEADER_ALIASES);

function normalizeHeader(h) {
  return String(h || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Split a single CSV line respecting double-quoted fields. */
function splitCsvLine(line) {
  const fields = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      fields.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current);
  return fields.map((f) => f.trim());
}

function mapHeaderIndex(headers) {
  const normalized = headers.map(normalizeHeader);
  const index = {};
  Object.entries({ ...HEADER_ALIASES, ...OPTIONAL_HEADER_ALIASES }).forEach(([canonical, aliases]) => {
    const idx = normalized.findIndex((h) => aliases.includes(h));
    if (idx >= 0) index[canonical] = idx;
  });
  return index;
}

function fieldAt(fields, colMap, key) {
  const idx = colMap[key];
  if (idx === undefined || idx >= fields.length) return '';
  return (fields[idx] || '').trim();
}

function parseDateValue(raw) {
  const clean = String(raw || '').trim();
  if (!clean) return '';
  const iso = /^\d{4}-\d{2}-\d{2}$/;
  if (iso.test(clean)) return clean;
  const dmy = clean.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (dmy) {
    const [, a, b, y] = dmy;
    const day = a.length === 2 ? a : a.padStart(2, '0');
    const month = b.length === 2 ? b : b.padStart(2, '0');
    return `${y}-${month}-${day}`;
  }
  return '';
}

/**
 * @param {string} text Raw CSV file contents
 * @returns {{ rows: Array, headers: string[], warnings: string[] }}
 */
export function parseMovieMagicCrewCsv(text) {
  const warnings = [];
  const raw = String(text || '').replace(/^\uFEFF/, '').trim();
  if (!raw) {
    throw new Error('The file is empty.');
  }

  const lines = raw.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) {
    throw new Error('The CSV must include a header row and at least one crew member.');
  }

  const headerFields = splitCsvLine(lines[0]);
  const colMap = mapHeaderIndex(headerFields);

  const missing = REQUIRED_HEADERS.filter((h) => colMap[h] === undefined);
  if (missing.length) {
    throw new Error(
      `Missing required columns: ${missing.join(', ')}. `
      + 'Expected: Name, Email, Job Title, Department, Project Name, Country, '
      + 'Schedule Start, Schedule End.',
    );
  }

  const rows = [];
  for (let i = 1; i < lines.length; i += 1) {
    const fields = splitCsvLine(lines[i]);
    if (fields.every((f) => !f.trim())) continue;

    const name = fieldAt(fields, colMap, 'name');
    const email = fieldAt(fields, colMap, 'email');
    const jobTitle = fieldAt(fields, colMap, 'job title');
    const department = fieldAt(fields, colMap, 'department');
    const projectName = fieldAt(fields, colMap, 'project name');
    const country = fieldAt(fields, colMap, 'country').toUpperCase();
    const productionCountry = colMap['production country'] !== undefined
      ? fieldAt(fields, colMap, 'production country').toUpperCase()
      : '';
    const scheduleStart = parseDateValue(fieldAt(fields, colMap, 'schedule start'));
    const scheduleEnd = parseDateValue(fieldAt(fields, colMap, 'schedule end'));
    const location = fieldAt(fields, colMap, 'location');
    const phone = fieldAt(fields, colMap, 'phone');

    if (!name && !email) {
      warnings.push(`Row ${i + 1}: skipped (no name or email).`);
      continue;
    }
    if (!projectName) {
      warnings.push(`Row ${i + 1}: skipped (missing project name).`);
      continue;
    }
    if (!country) {
      warnings.push(`Row ${i + 1}: skipped (missing country).`);
      continue;
    }
    if (!scheduleStart || !scheduleEnd) {
      warnings.push(`Row ${i + 1}: skipped (invalid or missing schedule dates).`);
      continue;
    }

    rows.push({
      name,
      email,
      phone,
      jobTitle,
      department,
      projectName,
      country,
      productionCountry,
      location,
      scheduleStart,
      scheduleEnd,
    });
  }

  if (!rows.length) {
    throw new Error('No valid crew rows found in this file.');
  }

  return { rows, headers: headerFields, warnings };
}
